
import React, { useState } from 'react';
import { getTemplateSets } from '@/utils/banner/templateUtils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { TemplateSet } from '@/types/bannerTemplate';

interface BannerTemplateSelectionStepProps {
  onSelectTemplate: (templateId: string, templateSetId: string) => void;
}

const BannerTemplateSelectionStep: React.FC<BannerTemplateSelectionStepProps> = ({ onSelectTemplate }) => {
  const [templateSets] = useState<TemplateSet[]>(getTemplateSets());
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);

  // Get unique categories from template sets
  const categories = Array.from(new Set(templateSets.map(set => set.category)));
  
  // Filter template sets by selected category
  const filteredTemplateSets = selectedCategory === 'all' 
    ? templateSets 
    : templateSets.filter(set => set.category === selectedCategory);

  const handleTemplateClick = (templateId: string, setId: string) => {
    setSelectedTemplateId(templateId);
    setSelectedSetId(setId);
  };

  const handleContinue = () => {
    if (selectedTemplateId && selectedSetId) {
      onSelectTemplate(selectedTemplateId, selectedSetId);
    }
  };

  return (
    <div className="flex flex-col space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Browse Templates</h2>
          <p className="text-gray-500">Select a template to customize</p>
        </div>
        <Tabs defaultValue="all" value={selectedCategory} onValueChange={setSelectedCategory} className="w-auto">
          <TabsList>
            <TabsTrigger value="all">All Categories</TabsTrigger>
            {categories.map(category => (
              <TabsTrigger key={category} value={category}>{category}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left panel - Template Sets */}
        <div className="lg:col-span-3">
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium mb-4">Template Sets ({filteredTemplateSets.length})</h3>
            <ScrollArea className="h-[600px] pr-4">
              <div className="space-y-4">
                {filteredTemplateSets.map(set => (
                  <div key={set.id} className="bg-white p-3 rounded-md shadow-sm">
                    <h4 className="font-medium">{set.name}</h4>
                    <p className="text-sm text-gray-500">{set.templates.length} templates</p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {set.tags?.slice(0, 3).map(tag => (
                        <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </div>

        {/* Right panel - Templates Grid */}
        <div className="lg:col-span-9">
          <div className="space-y-8">
            {filteredTemplateSets.map(templateSet => (
              <div key={templateSet.id} className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium">{templateSet.name}</h3>
                  <p className="text-sm text-gray-500">{templateSet.description}</p>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {templateSet.templates.map(template => {
                    const isSelected = selectedTemplateId === template.id;
                    const selectedClass = isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : '';
                    
                    return (
                      <Card 
                        key={template.id} 
                        className={`overflow-hidden hover:shadow-md transition-all cursor-pointer ${selectedClass}`}
                        onClick={() => handleTemplateClick(template.id, templateSet.id)}
                      >
                        <div className="aspect-[4/3] relative">
                          <img 
                            src={template.thumbnailUrl} 
                            alt={template.name} 
                            className="w-full h-full object-cover"
                          />
                          {template.formats.some(f => f.isAnimated) && (
                            <Badge className="absolute top-2 right-2 bg-blue-500">Animated</Badge>
                          )}
                          {isSelected && (
                            <div className="absolute inset-0 bg-blue-500/10 flex items-center justify-center">
                              <div className="bg-blue-500 text-white rounded-full p-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            </div>
                          )}
                        </div>
                        <CardContent className="p-4">
                          <h4 className="font-medium">{template.name}</h4>
                          <p className="text-sm text-gray-500">
                            {template.formats.length} sizes available
                          </p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-center mt-8">
        <Button 
          size="lg" 
          onClick={handleContinue} 
          disabled={!selectedTemplateId}
          className="px-8"
        >
          Continue with Selected Template
        </Button>
      </div>
    </div>
  );
};

export default BannerTemplateSelectionStep;
